import React, { useEffect, useMemo, useState } from "react";
import {
    SignOut,
    Boat,
    Package,
    Thermometer,
    ClockClockwise,
    CalendarBlank,
    CheckCircle,
    Warning,
    Fire,
    Drop,
    Gauge,
    UserCircle,
    ShieldCheck,
    ArrowsCounterClockwise,
    CaretRight,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import GrindrodLogo from "../components/GrindrodLogo";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";

/* ---------------------------------------------------------------------------
 * ClientDashboard
 * ---------------------------------------------------------------------------
 * Read-only dashboard for external clients. Shows ONLY:
 *   1. Their allocated Navitrade stockpile (stock on ground + trends)
 *   2. Vessels (upcoming + in-progress + recent completed) that match their
 *      client name.
 * Everything else is intentionally inaccessible. Headerless design — the
 * client has no sidebar, no portal navigation, no other menu items.
 * ------------------------------------------------------------------------ */

const PILE_COLORS = { A: "#9333EA", B: "#EA580C", C: "#2563EB", D: "#16A34A" };
const CONDITION_META = {
    "Good":       { color: "#16A34A", bg: "#E7F7EC", Icon: CheckCircle },
    "Fair":       { color: "#F59E0B", bg: "#FEF3C7", Icon: Warning },
    "Hot Spot":   { color: "#EF4444", bg: "#FEE2E2", Icon: Fire },
    "Combusting": { color: "#B91C1C", bg: "#FECACA", Icon: Fire },
    "Wet":        { color: "#0EA5E9", bg: "#E0F2FE", Icon: Drop },
};

const fmt = (n, d = 1) =>
    n === null || n === undefined || Number.isNaN(n)
        ? "—"
        : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });

const humanDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const humanDay = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso; }
};

const daysUntil = (iso) => {
    if (!iso) return null;
    try {
        const ms = new Date(iso).getTime() - Date.now();
        return Math.round(ms / 86_400_000);
    } catch { return null; }
};


export default function ClientDashboard() {
    const { user, logout } = useAuth();
    const [profile, setProfile] = useState(null);
    const [stockpile, setStockpile] = useState(null);
    const [vessels, setVessels] = useState({ upcoming: [], in_progress: [], recent_completed: [] });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const refresh = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setErr(null);
            try {
                const [me, pile, vs] = await Promise.all([
                    api.get("/client/me"),
                    api.get("/client/stockpile"),
                    api.get("/client/vessels"),
                ]);
                if (cancelled) return;
                setProfile(me.data);
                setStockpile(pile.data);
                setVessels(vs.data || { upcoming: [], in_progress: [], recent_completed: [] });
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    useEffect(() => {
        const t = setInterval(refresh, 60_000);
        return () => clearInterval(t);
    }, []);

    const utilColor = useMemo(() => {
        const p = stockpile?.utilisation_pct;
        if (p === null || p === undefined) return "#64748B";
        if (p >= 90) return "#EF4444";
        if (p >= 75) return "#F59E0B";
        return "#16A34A";
    }, [stockpile]);

    const trend = useMemo(() => {
        const rows = [...(stockpile?.history || [])].reverse().slice(-30);
        return rows.map((r) => ({
            t: new Date(r.recorded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
            tons: r.actual_tonnage,
            temp: r.temperature_c,
        }));
    }, [stockpile]);

    const nextVessel = vessels.upcoming?.[0];

    return (
        <div className="min-h-screen bg-slate-50" data-testid="client-dashboard">
            {/* Client-only header — no portal nav */}
            <header className="bg-[var(--grindrod-navy)] text-white shadow-md sticky top-0 z-30">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <GrindrodLogo className="h-10 w-10 shrink-0" />
                        <div className="min-w-0">
                            <div className="uppercase-label text-[9px] text-slate-300 tracking-[0.18em]">Client Portal</div>
                            <div className="font-display font-black uppercase tracking-wide text-sm sm:text-base truncate">
                                {profile?.client_name || "Client"}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            onClick={refresh}
                            title="Refresh"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-white/10 hover:bg-white/20 text-xs uppercase tracking-wide"
                            data-testid="client-refresh-btn"
                        >
                            <ArrowsCounterClockwise size={13} weight="bold" />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-300">
                            <UserCircle size={16} />
                            <span>{user?.name || profile?.name || "—"}</span>
                        </div>
                        <button
                            onClick={logout}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[var(--grindrod-red)] hover:bg-red-700 text-xs uppercase tracking-wide font-semibold"
                            data-testid="client-logout-btn"
                        >
                            <SignOut size={13} weight="bold" />
                            <span className="hidden sm:inline">Sign out</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5 space-y-5">
                {err && (
                    <div className="g-card p-4 border-l-4 !border-[var(--grindrod-red)] bg-[#FDE7E9] text-sm text-[var(--grindrod-red)]" data-testid="client-error">
                        {err}
                    </div>
                )}

                {/* Welcome banner */}
                <div className="g-card overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-[var(--grindrod-navy)] to-[#1e3a8a] text-white px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <div className="uppercase-label text-[10px] text-slate-300 tracking-[0.16em]">Welcome</div>
                            <div className="font-display font-black text-lg sm:text-xl">
                                {user?.name || profile?.name || "Client user"}
                            </div>
                            <div className="text-xs text-slate-300 mt-1 flex items-center gap-1.5">
                                <ShieldCheck size={13} weight="fill" />
                                Secure, read-only view of your stock-on-ground and allocated vessels.
                            </div>
                        </div>
                        {nextVessel && (
                            <div className="rounded-sm bg-white/10 px-4 py-2 text-right">
                                <div className="uppercase-label text-[9px] text-slate-300">Next upcoming vessel</div>
                                <div className="font-display font-bold text-sm">{nextVessel.vessel_name}</div>
                                <div className="text-[11px] text-slate-300 tabular">
                                    {humanDay(nextVessel.etb || nextVessel.start_datetime)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {loading && !stockpile ? (
                    <div className="g-card p-8 text-center text-slate-400 text-sm">Loading your dashboard…</div>
                ) : (
                    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
                        {/* Stockpile card */}
                        <StockpileCard pile={stockpile} utilColor={utilColor} trend={trend} />

                        {/* Vessels panel */}
                        <VesselsPanel vessels={vessels} stockpile={stockpile} />
                    </div>
                )}

                {/* Readiness insight — stock vs next vessel */}
                {stockpile && nextVessel && (
                    <ReadinessBanner pile={stockpile} nextVessel={nextVessel} />
                )}

                <footer className="text-center text-[11px] text-slate-400 py-4 border-t border-slate-200 mt-4">
                    Grindrod Terminals · Richards Bay · Client view
                    <span className="mx-2">·</span>
                    Data updates automatically every minute.
                </footer>
            </main>
        </div>
    );
}


function StockpileCard({ pile, utilColor, trend }) {
    if (!pile) {
        return (
            <div className="g-card p-6 text-slate-400 text-sm">No stockpile allocated to your account.</div>
        );
    }
    const latest = pile.latest;
    const cfg = pile.config || {};
    const util = pile.utilisation_pct;
    const cond = latest?.condition;
    const meta = cond ? CONDITION_META[cond] : null;
    const color = PILE_COLORS[pile.pile_id];
    return (
        <div className="space-y-4" data-testid="client-stockpile-card">
            <div className="g-card overflow-hidden shadow-sm">
                <div className="flex items-stretch">
                    <div className="w-16 flex items-center justify-center" style={{ background: color }}>
                        <span className="text-white font-display font-black text-3xl">{pile.pile_id}</span>
                    </div>
                    <div className="flex-1 bg-[var(--grindrod-navy)] text-white px-4 py-3 text-center">
                        <div className="uppercase-label text-[9px] text-slate-300 tracking-[0.16em]">Your stockpile</div>
                        <div className="font-display font-black uppercase tracking-wide text-base sm:text-lg mt-0.5">
                            {pile.client_name || cfg.client_allocation || `Stockpile ${pile.pile_id}`}
                        </div>
                        <div className="text-[11px] text-slate-300 mt-1 tabular">
                            Capacity {fmt(cfg.capacity_tons, 0)} t
                        </div>
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    {/* Big KPI row */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <KpiBlock
                            label="Stock on ground"
                            value={`${fmt(latest?.actual_tonnage, 0)} t`}
                            accent={color}
                            big
                        />
                        <KpiBlock
                            label="Utilisation"
                            value={util === null || util === undefined ? "—" : `${util}%`}
                            accent={utilColor}
                            big
                        />
                        <KpiBlock
                            label="Available"
                            value={`${fmt(pile.available_tons, 0)} t`}
                            accent="var(--grindrod-navy)"
                            big
                        />
                    </div>

                    {/* Capacity bar */}
                    <div>
                        <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                            <span className="uppercase tracking-wide">Capacity used</span>
                            <span className="tabular font-semibold" style={{ color: utilColor }}>
                                {util !== null && util !== undefined ? `${util}%` : "—"}
                            </span>
                        </div>
                        <div className="h-3 rounded-sm bg-slate-100 overflow-hidden">
                            <div
                                className="h-full transition-all"
                                style={{ width: `${Math.min(100, util || 0)}%`, background: utilColor }}
                            />
                        </div>
                    </div>

                    {/* Secondary stats */}
                    <div className="grid grid-cols-3 gap-2">
                        <MiniStat label="Grade" value={latest?.grade || "—"} />
                        <MiniStat
                            label={<span className="inline-flex items-center gap-1"><Thermometer size={10} />Temp</span>}
                            value={latest?.temperature_c !== undefined && latest?.temperature_c !== null ? `${fmt(latest.temperature_c, 1)} °C` : "—"}
                            accent={latest?.temperature_c > 60 ? "#EF4444" : latest?.temperature_c > 45 ? "#F59E0B" : undefined}
                        />
                        <MiniStat
                            label="Condition"
                            value={cond || "—"}
                            accent={meta?.color}
                            bg={meta?.bg}
                        />
                    </div>

                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 justify-center text-center">
                        <ClockClockwise size={12} />
                        {latest
                            ? <span>Last inspection <span className="tabular">{humanDate(latest.recorded_at)}</span></span>
                            : <span className="italic">No inspections logged yet.</span>}
                    </div>
                </div>
            </div>

            {/* Trend chart */}
            <div className="g-card overflow-hidden shadow-sm">
                <div className="g-card-header-dark">
                    <Gauge size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">Tonnage trend (last 30 readings)</div>
                </div>
                <div className="p-3 h-56">
                    {trend.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trend} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                                <XAxis dataKey="t" stroke="#64748B" fontSize={10} tick={{ fontSize: 10 }} minTickGap={30} />
                                <YAxis stroke="#64748B" fontSize={10} tick={{ fontSize: 10 }} />
                                <Tooltip contentStyle={{ fontSize: 11 }} />
                                {cfg.capacity_tons && (
                                    <ReferenceLine y={cfg.capacity_tons} stroke={color} strokeDasharray="4 4"
                                        label={{ value: "Capacity", fill: color, fontSize: 10, position: "insideTopRight" }} />
                                )}
                                <Line type="monotone" dataKey="tons" stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-slate-400 text-xs">
                            Not enough readings for a trend yet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function VesselsPanel({ vessels, stockpile }) {
    const upcoming = vessels.upcoming || [];
    const live = vessels.in_progress || [];
    const done = vessels.recent_completed || [];
    return (
        <div className="space-y-4" data-testid="client-vessels-panel">
            {/* Live */}
            {live.length > 0 && (
                <div className="g-card overflow-hidden shadow-sm">
                    <div className="g-card-header-dark bg-gradient-to-r from-[#16A34A] to-[#15803D]">
                        <Boat size={16} weight="fill" />
                        <div className="font-display font-bold uppercase tracking-wide">Currently loading</div>
                        <span className="ml-auto text-xs font-semibold tabular">{live.length}</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {live.map((v) => <VesselRow key={v.id} v={v} tag="active" />)}
                    </div>
                </div>
            )}

            {/* Upcoming */}
            <div className="g-card overflow-hidden shadow-sm">
                <div className="g-card-header-dark">
                    <CalendarBlank size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">Upcoming vessels</div>
                    <span className="ml-auto text-xs font-semibold tabular">{upcoming.length}</span>
                </div>
                {upcoming.length === 0 ? (
                    <div className="p-6 text-center text-slate-400 text-sm italic">
                        No upcoming vessels have been scheduled against your account.
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {upcoming.map((v) => (
                            <VesselRow
                                key={v.id}
                                v={v}
                                tag="upcoming"
                                stockpile={stockpile}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Recent completed */}
            {done.length > 0 && (
                <div className="g-card overflow-hidden shadow-sm">
                    <div className="g-card-header-dark bg-slate-700">
                        <CheckCircle size={16} weight="fill" />
                        <div className="font-display font-bold uppercase tracking-wide">Recently completed</div>
                        <span className="ml-auto text-xs font-semibold tabular">{done.length}</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {done.map((v) => <VesselRow key={v.id} v={v} tag="completed" />)}
                    </div>
                </div>
            )}
        </div>
    );
}


function VesselRow({ v, tag, stockpile }) {
    const whenIso = v.etb || v.start_datetime || v.end_datetime;
    const dLeft = tag === "upcoming" ? daysUntil(whenIso) : null;
    const stockOnHand = stockpile?.latest?.actual_tonnage;
    const shortfall =
        tag === "upcoming" && v.tonnage && stockOnHand !== undefined && stockOnHand !== null
            ? v.tonnage - stockOnHand
            : null;
    const shortfallColor = shortfall === null
        ? null
        : shortfall > 0 ? "#EF4444" : "#16A34A";

    return (
        <div className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`client-vessel-row-${v.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-sm text-[var(--grindrod-navy)] truncate">
                        {v.vessel_name}
                    </div>
                    {v.division && (
                        <span className="g-pill g-pill-navy text-[9px] px-1.5 py-0.5">{v.division}</span>
                    )}
                    {tag === "active" && (
                        <span className="g-pill g-pill-green text-[9px] px-1.5 py-0.5 inline-flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-200 animate-pulse" /> LOADING
                        </span>
                    )}
                    {tag === "completed" && (
                        <span className="g-pill text-[9px] px-1.5 py-0.5 bg-slate-200 text-slate-700">COMPLETED</span>
                    )}
                    {tag === "upcoming" && dLeft !== null && (
                        <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm"
                            style={{
                                color: dLeft <= 3 ? "#B91C1C" : dLeft <= 7 ? "#B45309" : "#1E40AF",
                                background: dLeft <= 3 ? "#FEE2E2" : dLeft <= 7 ? "#FEF3C7" : "#DBEAFE",
                            }}
                        >
                            {dLeft < 0 ? `${Math.abs(dLeft)} days ago` : dLeft === 0 ? "Today" : `In ${dLeft} days`}
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 tabular flex items-center gap-2 flex-wrap">
                    <span>{v.product}</span>
                    <span>·</span>
                    <span>{fmt(v.tonnage, 0)} t</span>
                    <span>·</span>
                    <span>{humanDate(whenIso)}</span>
                    {tag === "active" && v.total_tons_loaded !== undefined && v.total_tons_loaded !== null && (
                        <>
                            <span>·</span>
                            <span className="text-[var(--grindrod-navy)] font-semibold">
                                Loaded {fmt(v.total_tons_loaded, 0)} t
                            </span>
                        </>
                    )}
                </div>
                {shortfall !== null && (
                    <div className="text-[11px] mt-1 tabular font-semibold" style={{ color: shortfallColor }}>
                        {shortfall > 0
                            ? <>Need an additional <span className="font-black">{fmt(shortfall, 0)} t</span> before loading</>
                            : <>Enough stock on ground — <span className="font-black">{fmt(-shortfall, 0)} t surplus</span></>
                        }
                    </div>
                )}
            </div>
            <CaretRight size={14} className="text-slate-300 shrink-0" />
        </div>
    );
}


function ReadinessBanner({ pile, nextVessel }) {
    const stock = pile.latest?.actual_tonnage || 0;
    const needed = nextVessel.tonnage || 0;
    const gap = needed - stock;
    const ready = gap <= 0;
    const bg = ready ? "#E7F7EC" : gap <= needed * 0.2 ? "#FEF3C7" : "#FEE2E2";
    const fg = ready ? "#15803D" : gap <= needed * 0.2 ? "#B45309" : "#B91C1C";
    return (
        <div
            className="rounded-md border-l-4 px-4 py-3 shadow-sm flex items-start gap-3"
            style={{ background: bg, borderColor: fg }}
            data-testid="client-readiness-banner"
        >
            {ready ? <CheckCircle size={22} weight="fill" style={{ color: fg }} /> : <Warning size={22} weight="fill" style={{ color: fg }} />}
            <div className="min-w-0">
                <div className="font-display font-black uppercase tracking-wide text-sm" style={{ color: fg }}>
                    {ready ? "You are vessel-ready" : "Stock top-up required"}
                </div>
                <div className="text-xs mt-0.5 text-slate-700">
                    <span className="font-semibold">{nextVessel.vessel_name}</span> is scheduled for{" "}
                    <span className="tabular">{humanDay(nextVessel.etb || nextVessel.start_datetime)}</span>{" "}
                    and will load <span className="tabular font-semibold">{fmt(needed, 0)} t</span>.
                    Current stock on ground is <span className="tabular font-semibold">{fmt(stock, 0)} t</span>.
                    {ready
                        ? <> You have a surplus of <span className="tabular font-semibold">{fmt(-gap, 0)} t</span>.</>
                        : <> Deliver <span className="tabular font-semibold">{fmt(gap, 0)} t</span> more to Grindrod before ETB.</>
                    }
                </div>
            </div>
        </div>
    );
}


function KpiBlock({ label, value, accent = "var(--grindrod-navy)", big }) {
    return (
        <div className="rounded-sm border border-slate-100 px-2 py-2 text-center">
            <div className="uppercase-label text-[9px] text-slate-500 tracking-[0.14em]">{label}</div>
            <div
                className={`font-display font-black tabular mt-1 ${big ? "text-xl sm:text-2xl" : "text-base"}`}
                style={{ color: accent }}
            >
                {value}
            </div>
        </div>
    );
}


function MiniStat({ label, value, accent = "var(--grindrod-navy)", bg }) {
    return (
        <div
            className="rounded-sm border border-slate-100 px-2 py-1.5 text-center"
            style={bg ? { background: bg, borderColor: "transparent" } : {}}
        >
            <div className="uppercase-label text-[8px] text-slate-500 tracking-[0.14em]">{label}</div>
            <div className="font-display font-bold text-[13px] tabular truncate mt-0.5" style={{ color: accent }}>
                {value}
            </div>
        </div>
    );
}
